import { hasVersionHistory } from "@/domains/documents/utils/versionHistory";
import { BulkActionBar } from "@/domains/documents/components/molecules/BulkActionBar";
import { verifiedSelectionState } from "@/domains/documents/hooks/useDocumentVerification";
import { cn } from "@/lib/utils";
import { observer } from "mobx-react-lite";
import type { DocumentTablePresentation } from "./documentTablePresentation";
import type { DocumentWithTags } from "./documentTableTypes";
import { verifyMarks } from "./documentTableValues";
export const TableBulkActions = observer(function TableBulkActions(
  props: DocumentTablePresentation
) {
  const {
    showCheckbox,
    store,
    hideBulkActionBar,
    selectedFolderIds,
    displayDocuments,
    onRenameDocumentSubmit,
    canRenameDocument,
    allDocuments,
    isRestore,
    canVerifyDocument,
    setVerified,
  } = props;
  return (
    showCheckbox &&
    store &&
    !hideBulkActionBar &&
    (() => {
      const singleSelectedDoc =
        store.selectedCount === 1 && selectedFolderIds.length === 0
          ? displayDocuments.find((d) => store.isSelected(d.id))
          : undefined;
      const canRenameSingle =
        !!singleSelectedDoc &&
        !!onRenameDocumentSubmit &&
        (canRenameDocument?.(singleSelectedDoc) ?? false);
      // Resolve from allDocuments, not displayDocuments — the selection
      // count on the bar includes selected container rows, so the write
      // (and the permission gate) must cover them too.
      const selectedDocs = allDocuments.filter((d) => store.isSelected(d.id));
      const canVerifySelection =
        !isRestore && selectedDocs.length > 0 && selectedDocs.every((d) => canVerifyDocument(d));
      const selectionVerifiedState = verifiedSelectionState(selectedDocs);
      const selectedVerify = {
        verifiedState: selectionVerifiedState,
        onToggleVerified: canVerifySelection
          ? () => void setVerified(verifyMarks(selectedDocs), selectionVerifiedState !== "all")
          : undefined,
      };
      return (
        // Out of flow: the bar itself is fixed, so this wrapper only dims it
        // while the list refreshes — and in flow its stack spacing would add
        // blank space under the table.
        <TableBulkBar
          {...props}
          singleSelectedDoc={singleSelectedDoc}
          canRenameSingle={canRenameSingle}
          selectedVerify={selectedVerify}
        />
      );
    })()
  );
});

export const TableBulkBar = observer(function TableBulkBar(
  props: DocumentTablePresentation & {
    singleSelectedDoc: DocumentWithTags | undefined;
    canRenameSingle: boolean;
    selectedVerify: {
      verifiedState: ReturnType<typeof verifiedSelectionState>;
      onToggleVerified: (() => void) | undefined;
    };
  }
) {
  const {
    showLoadingOverlay,
    store,
    selectedFolderIds,
    isSelectionPending,
    canMoveToTrash,
    runBulkDelete,
    runBulkDownload,
    onFolderSelect,
    handleComparePdfs,
    canComparePdfs,
    canCompareIfcs,
    handleDiffOverview,
    canViewDrawing,
    isRestore,
    handleViewSelectedDrawing,
    canCompareDrawings,
    handleCompareSelectedDrawings,
    onShare,
    signingEnabled,
    openSignDialog,
    onMoveCopy,
    handleAskFranklinClick,
    hostActionOnSelection,
    singleSelectedDoc,
    canRenameSingle,
    documentRename,
    onAddNewVersions,
    addVersions,
    selectedVerify,
    canOpenComments,
    openComments,
  } = props;
  return (
    <div className={cn("absolute", showLoadingOverlay && "pointer-events-none opacity-60")}>
      <BulkActionBar
        selectedCount={store.selectedCount + selectedFolderIds.length}
        documentCount={store.selectedCount}
        isSelectionPending={isSelectionPending}
        onDelete={canMoveToTrash ? runBulkDelete : undefined}
        onDownload={runBulkDownload}
        onClearSelection={() => {
          store.clearSelection();
          selectedFolderIds.forEach((id) => onFolderSelect?.(id, false));
        }}
        onCompare={handleComparePdfs}
        canCompare={canComparePdfs}
        canCompareModels={canCompareIfcs}
        onDiffOverview={handleDiffOverview}
        canViewDrawing={canViewDrawing && selectedFolderIds.length === 0 && !isRestore}
        onViewDrawing={handleViewSelectedDrawing}
        canCompareDrawings={canCompareDrawings && selectedFolderIds.length === 0 && !isRestore}
        onCompareDrawings={handleCompareSelectedDrawings}
        onShare={onShare}
        onSignature={signingEnabled ? () => void openSignDialog() : undefined}
        onMoveCopy={onMoveCopy}
        onAskFranklin={handleAskFranklinClick}
        hostAction={selectedFolderIds.length === 0 ? hostActionOnSelection : undefined}
        onRename={
          singleSelectedDoc && canRenameSingle
            ? () =>
                documentRename.startRename({
                  id: singleSelectedDoc.id,
                  filename: singleSelectedDoc.filename,
                })
            : undefined
        }
        onAddNewVersion={
          singleSelectedDoc && onAddNewVersions
            ? () => addVersions.openFor(singleSelectedDoc)
            : undefined
        }
        opensVersioning={!!singleSelectedDoc && hasVersionHistory(singleSelectedDoc)}
        onToggleVerified={selectedVerify.onToggleVerified}
        verifiedState={selectedVerify.verifiedState}
        onComments={
          singleSelectedDoc && canOpenComments ? () => openComments(singleSelectedDoc) : undefined
        }
      />
    </div>
  );
});
